import { useCallback, useEffect, useRef } from 'react';

import { MARKETPLACE_INFINITE_SCROLL_THRESHOLD } from '../constants/marketplace';
import { DomEventEnum, IUseInfiniteScrollOptions } from '../types/shell';
import { hasReachedScrollThreshold } from '../util/ui';

// Capture-phase and passive so it also fires inside an embed whose own element is the scroller.
// The guards live in a ref so the listener is attached once and still reads current values.
export const useInfiniteScroll = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  threshold = MARKETPLACE_INFINITE_SCROLL_THRESHOLD,
  recheckKey,
}: IUseInfiniteScrollOptions): void => {
  const optionsRef = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage, threshold });
  optionsRef.current = { hasNextPage, isFetchingNextPage, fetchNextPage, threshold };

  const handleScroll = useCallback(() => {
    const current = optionsRef.current;

    if (!current.hasNextPage || current.isFetchingNextPage) {
      return;
    }

    const { scrollTop, clientHeight, scrollHeight } = document.scrollingElement || document.documentElement;

    if (hasReachedScrollThreshold({ scrollTop, clientHeight, scrollHeight }, current.threshold)) {
      current.fetchNextPage();
    }
  }, []);

  useEffect(() => {
    window.addEventListener(DomEventEnum.Scroll, handleScroll, { passive: true, capture: true });
    window.addEventListener(DomEventEnum.Resize, handleScroll);

    return () => {
      window.removeEventListener(DomEventEnum.Scroll, handleScroll, { capture: true });
      window.removeEventListener(DomEventEnum.Resize, handleScroll);
    };
  }, [handleScroll]);

  // A viewport tall enough to swallow a whole page never scrolls, so re-check after each page and
  // whenever a client-side filter shortens the list.
  useEffect(() => {
    handleScroll();
  }, [handleScroll, hasNextPage, isFetchingNextPage, recheckKey]);
};
